import { z } from 'zod';

// Helper function to validate tiers are in ascending order
// const validateTiersOrder = (tiers: { quantity: number; value: number }[]) => {
//   const issues: { path: string[]; message: string }[] = [];
  
//   for (let i = 1; i < tiers.length; i++) {
//     const prevTier = tiers[i - 1];
//     const currentTier = tiers[i];
    
//     if (currentTier.quantity <= prevTier.quantity) {
//       issues.push({ 
//         path: [`${i}.quantity`],
//         message: `Quantity must be greater than ${prevTier.quantity}`
//       });
//     }
    
//     if (currentTier.value <= prevTier.value) {
//       issues.push({
//         path: [`${i}.value`],
//         message: `Value must be greater than ${prevTier.value}`
//       });
//     }
//   }
  
//   return issues.length === 0 ? true : issues;
// };

// const TierSchema = z.object({
//   quantity: z.number()
//     .min(1, 'Quantity must be at least 1')
//     .int('Quantity must be a whole number'),
//   value: z.number()
//     .min(0, 'Value must be at least 0')
// });

// const TiersArraySchema = z.array(TierSchema)
//   .min(1, 'At least one tier is required')
//   .superRefine((tiers, ctx) => {
//     const result = validateTiersOrder(tiers);
//     if (result !== true) {
//       result.forEach(issue => {
//         ctx.addIssue({
//           code: z.ZodIssueCode.custom,
//           message: issue.message,
//           path: issue.path
//         });
//       });
//     }
//   });

// Strict date validation helper
const isValidDate = (d: unknown): d is Date => 
  d instanceof Date && !isNaN(d.getTime());

// Individual tier schema with required dates
export const DateRangeTierSchema = z.object({
  start_date: z.custom<Date>(
    val => isValidDate(val) || val === null,
    { message: "Valid start date required in discount tiers" }
  ).nullable(),
  end_date: z.custom<Date>(
    val => isValidDate(val) || val === null,
    { message: "Valid end date required in discount tiers" }
  ).nullable(),
  type: z.literal('percentage').default('percentage'),
  value: z.number()
    .int("Must be whole percentage")
    .min(0, "Discount percentage cannot be less than 0%")
    .max(100, "Discount percentage cannot be greater than 100%")
    .optional()
}).refine(
  data => {
    // If either date is null, validation passes at this stage
    if (data.start_date === null || data.end_date === null) {
      return true;
    }
    // Both dates exist, check that end is after start
    return data.end_date > data.start_date;
  },
  {
    message: "End date must be after start date in discount tiers",
    path: ["end_date"]
  }
);

// Full discount schema with proper error paths
export const DiscountFormSchema = z.object({
  discount_type: z.literal('product_creation_based').default('product_creation_based'),
  discount_string: z.string().trim().nullish(),
  product_creation_based_tiers: z.array(DateRangeTierSchema)
    .min(1, "At least one tier required")
    .superRefine((tiers, ctx) => {
      // Track original indices for error reporting
      const withIndices = tiers.map((tier, index) => ({
        ...tier,
        originalIndex: index
      }));

      // Sort tiers by start date (earliest first)
      const sorted = [...withIndices].sort((a, b) => 
        (!a.start_date ? 0 : a.start_date.getTime()) - (!b.start_date ? 0 : b.start_date.getTime())
      );

      // Sweep line algorithm for overlap detection
      const activeTiers: typeof sorted = [];
      for (const current of sorted) {
        // Check against all active tiers that might overlap
        for (const active of activeTiers) {
          if ((!current.start_date ? 0 : current.start_date.getTime()) < (!active.end_date ? 0 : active.end_date.getTime())) {
            // Add errors to both overlapping tiers
            [active, current].forEach((tier) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Overlaps with tier ${tier.originalIndex + 1}`,
                path: [
                  "product_creation_based_tiers",
                  tier.originalIndex,
                  "end_date"
                ]
              });
            });
          }
        }

        // Maintain active tiers sorted by end date
        activeTiers.push(current);
        activeTiers.sort((a, b) => 
          (!a.end_date ? 0 : a.end_date.getTime()) - (!b.end_date ? 0 : b.end_date.getTime())
        );
        
        // Remove non-overlapping tiers
        while (activeTiers[0]?.end_date && 
          (!activeTiers[0]?.end_date ? 0 : activeTiers[0]?.end_date.getTime()) <= (!current.start_date ? 0 : current.start_date.getTime())) {
          activeTiers.shift();
        }
      }
    })
});

// Type exports
export type IDiscountForm = z.infer<typeof DiscountFormSchema>;
export type IDateRangeTier = z.infer<typeof DateRangeTierSchema>;